/**
 * Everything goes through CloudFront on one host, so there is no base URL, no
 * CORS and no token handling: the session lives in HttpOnly cookies the browser
 * attaches on its own and JavaScript cannot read.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

interface ProblemBody {
  error?: string;
  message?: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    // Already the default, but a future absolute URL must not silently start
    // omitting the session cookie.
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text.length > 0 ? safeParse(text) : {};

  if (!response.ok) {
    const problem = parsed as ProblemBody;
    throw new ApiError(
      response.status,
      problem.error ?? 'error',
      problem.message ?? 'Something went wrong. Please try again.',
    );
  }

  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export interface SiteInfo {
  title: string;
  tagline?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactInstagram?: string;
  splashNote?: string;
}

export interface ManifestAsset {
  id: string;
  type: 'photo' | 'video';
  w: number;
  h: number;
  lqip?: string;
  capturedAt?: string;
  filename: string;
  variants: Record<string, string>;
  durationMs?: number;
}

export interface Manifest {
  portfolio: {
    id: string;
    title: string;
    welcomeMessage?: string;
    coverAssetId?: string;
    downloads: { original: boolean; fb: boolean; ig: boolean };
    bundles: { size: 'original' | 'fb'; path: string; bytes: number }[];
  };
  assets: ManifestAsset[];
}
